/**
 * Traces command — directory module
 *
 * Subcommands: list, get, export (real SDK), analyze, stream (coming soon)
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { handleError } from '../../lib/errors.js';
import { traceLink } from '../../lib/ui-links.js';
import type { Trace } from '../../types/index.js';
import { registerExportCommand } from './export.js';
import { registerAnalyzeCommand } from './analyze.js';
import { registerStreamCommand } from './stream.js';

interface ListOptions {
  prompt?: string;
  source?: string;
  limit: string;
}

export function createTracesCommand(): Command {
  const traces = new Command('traces')
    .description('View and analyze traces')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent traces list
  ${chalk.dim('$')} mutagent traces list --prompt <prompt-id>
  ${chalk.dim('$')} mutagent traces get <trace-id>
  ${chalk.dim('$')} mutagent traces analyze <prompt-id>
  ${chalk.dim('$')} mutagent traces export --format json --output traces.json
    `);

  // ── list (inline — small, real SDK) ──────────────────────────────────
  traces
    .command('list')
    .description('List traces')
    .option('-p, --prompt <id>', 'Filter by prompt ID')
    .option('-s, --source <source>', 'Filter by trace source (e.g., claude-code, sdk, langchain)')
    .option('-l, --limit <n>', 'Limit results', '50')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent traces list
  ${chalk.dim('$')} mutagent traces list --prompt <prompt-id>
  ${chalk.dim('$')} mutagent traces list --source claude-code --json
  ${chalk.dim('$')} mutagent traces list --limit 10 --json

${chalk.dim('Tip: Filter by prompt to see traces for a specific prompt version.')}
    `)
    .action(async (options: ListOptions) => {
      const isJson = getJsonFlag(traces);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const tracesList = await client.listTraces({
          promptId: options.prompt,
          source: options.source,
        });

        const limit = parseInt(options.limit, 10) || 50;
        const limited = tracesList.slice(0, limit);

        if (isJson) {
          const withLinks = (limited as Trace[]).map((t) => ({
            id: t.id,
            promptId: t.promptId,
            status: t.status,
            latency: t.latency,
            tokens: t.tokens,
            timestamp: t.timestamp,
            _links: { view: traceLink(t.id) },
          }));
          output.output(withLinks);
        } else {
          const formatted = (limited as Trace[]).map((t) => ({
            id: t.id,
            promptId: t.promptId,
            latency: `${String(t.latency)}ms`,
            tokens: `${String(t.tokens.input)}/${String(t.tokens.output)}`,
            timestamp: new Date(t.timestamp).toLocaleString(),
            url: traceLink(t.id),
          }));
          output.output(formatted);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // ── get (inline — small, real SDK) ───────────────────────────────────
  traces
    .command('get')
    .description('Get trace details')
    .argument('<id>', 'Trace ID')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent traces get <trace-id>
  ${chalk.dim('$')} mutagent traces get <trace-id> --json

${chalk.dim('Returns full trace details including spans, tokens, and latency.')}
    `)
    .action(async (id: string) => {
      const isJson = getJsonFlag(traces);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const trace = await client.getTrace(id);
        if (isJson) {
          output.output({ ...trace as object, _links: { view: traceLink(id) } });
        } else {
          output.output(trace);
          output.info(`View: ${traceLink(id)}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // ── subcommands from separate files ──────────────────────────────────
  registerExportCommand(traces);
  registerAnalyzeCommand(traces);
  registerStreamCommand(traces);

  return traces;
}
